import React, { useContext, useCallback, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Item, Select, Button, Checkbox, Input } from "semantic-ui-react";
import omit from "lodash/omit";
import ConfirmPopup from "../../../../../../../components/ConfirmPopup";
import "./BotsConfiguration.scss";
import toastr from "toastr";
import ConfigurationContext from "../../providers/ConfigurationProvider/Configuration.context";
import { UsersContext } from "../../../../../../../common/UsersContext";
import { getDateFormat } from "../../../../../../../common/date-format";
import { logServerError } from "../../../../../../../common/logs";
import apis from "../../../../../../../apis";
import { filterInvalidProductProps } from "../../../../routers/Products/constants/productNames";

const PORTAL_ACCOUNT_SUPPORTED_APIS = ["ds82", "ds11"];
const DEFAULT_RETRY_CAP = {
  manualPause: false,
  lastPauseChangeBy: null,
  lastPauseChangeAt: null,
};

const BotsConfiguration = () => {
  const {
    botConfigs: defaultBotConfigs,
    creditCards,
    setBotConfigs: setDefaultBotConfigs,
  } = useContext(ConfigurationContext);
  const currentUser = useSelector((state) => state.currentUser);
  const { allUsers = [] } = useContext(UsersContext);
  const [botConfigs, setBotConfigs] = useState(defaultBotConfigs);
  const [isPausing, setIsPausing] = useState(false);

  const editBotConigurationHandler = useCallback(async () => {
    try {
      const payload = {
        ...botConfigs,
        ds82: botConfigs.ds82 ? omit(botConfigs.ds82, ["retryCap"]) : botConfigs.ds82,
      };
      await apis.configuration.botConfig.editBotConfiguration(payload);
      setDefaultBotConfigs(botConfigs);
      toastr.success("Configurations have been updated!");
    } catch (err) {
      logServerError(err);
    }
  }, [botConfigs, setDefaultBotConfigs]);

  const discardHandler = useCallback(() => {
    setBotConfigs(defaultBotConfigs);
  }, [setBotConfigs, defaultBotConfigs]);

  const creditCardOptions = useMemo(
    () =>
      Object.keys(creditCards).map((creditCard, index) => ({
        key: index,
        value: creditCards[creditCard]._id,
        text: creditCards[creditCard].cardName,
        description: `**** **** **** ${creditCards[creditCard].last4Digits}`,
      })),
    [creditCards],
  );

  const changeBotPropery = useCallback(
    (key, prop, value) => {
      const currentConfig = botConfigs[key];
      setBotConfigs({
        ...botConfigs,
        [key]: { ...currentConfig, [prop]: value },
      });
    },
    [setBotConfigs, botConfigs],
  );

  const renderAutosubmitOptions = useCallback(
    (key) => {
      const {
        autoRun: { enabled, delay },
        autoRun,
      } = botConfigs[key];
      return (
        <React.Fragment>
          <Item.Content>
            <Item.Meta>Bot Autosubmit Settings</Item.Meta>
            <Item.Description className="bot-autosubmit-content-wrapper">
              <div className="bot-autosubmit-toggle">
                <Checkbox
                  toggle
                  onChange={(event, { checked }) =>
                    changeBotPropery(key, "autoRun", {
                      ...autoRun,
                      enabled: checked,
                    })
                  }
                  checked={enabled}
                />
                {enabled ? "enabled" : "disabled"}
              </div>
              <div className="bot-autosubmit-input">
                <Input
                  name="delay"
                  type="text"
                  disabled={!enabled}
                  value={delay}
                  source={key}
                  onChange={(event, { value }) => {
                    if (!value || /^[0-9]*([.,][0-9]*)?$/.test(value)) {
                      changeBotPropery(key, "autoRun", {
                        ...autoRun,
                        delay: value,
                      });
                    }
                  }}
                />
                Autorun Delay (minutes)
              </div>
            </Item.Description>
          </Item.Content>
        </React.Fragment>
      );
    },
    [botConfigs, changeBotPropery],
  );

  const renderChecker = useCallback(
    (key) => {
      const {
        statusChecker: { timeToNextAttemptArray, attemptsNumber, enabled },
        statusChecker,
      } = botConfigs[key];

      return (
        key === "esta" && (
          <React.Fragment>
            <Item.Content>
              <Item.Meta>Bot Status Checker Settings</Item.Meta>
              <Item.Description className="bot-autosubmit-content-wrapper">
                <div className="bot-autosubmit-toggle">
                  <Checkbox
                    toggle
                    onChange={(event, { checked }) =>
                      changeBotPropery(key, "statusChecker", {
                        ...statusChecker,
                        enabled: checked,
                      })
                    }
                    checked={enabled}
                  />
                  {enabled ? "enabled" : "disabled"}
                </div>
                <div className="bot-autosubmit-input">
                  Attempts number
                  <br/>
                  <Input
                    name="attemptsNumber"
                    type="number"
                    min="0"
                    value={attemptsNumber}
                    onChange={(_, { value }) => {
                      changeBotPropery(key, "statusChecker", {
                        ...statusChecker,
                        attemptsNumber: value,
                      });
                    }}
                    onBlur={() => {
                      const numberValue = parseInt(attemptsNumber);
                      if (!numberValue || numberValue < 0) {
                        changeBotPropery(key, "statusChecker", {
                          ...statusChecker,
                          attemptsNumber: 0,
                        });
                      }
                    }}
                  />
                </div>
              </Item.Description>
              <div className="bot-next-attempt">
                Time to next attempt - Time in minutes to the next bot attempt
                {timeToNextAttemptArray.map((el, index) => (
                  <div key={index} className="bot-next-attempt__input">
                    <Input
                      value={el}
                      type="number"
                      min="1"
                      onChange={(_, { value }) => {
                        const newValue = [...timeToNextAttemptArray];
                        newValue.splice(index, 1, value);
                        changeBotPropery(key, "statusChecker", {
                          ...statusChecker,
                          timeToNextAttemptArray: newValue,
                        });
                      }}
                      onBlur={() => {
                        const numberValue = parseInt(
                          timeToNextAttemptArray[index],
                        );
                        if (!numberValue || numberValue < 1) {
                          const newValue = [...timeToNextAttemptArray];
                          newValue.splice(index, 1, 1);
                          changeBotPropery(key, "statusChecker", {
                            ...statusChecker,
                            timeToNextAttemptArray: newValue,
                          });
                        }
                      }}
                    />
                    <Button
                      type="button"
                      style={{ marginLeft: "8px", padding: "8px" }}
                      onClick={() => {
                        const newValue = [...timeToNextAttemptArray];
                        newValue.splice(index, 1);
                        changeBotPropery(key, "statusChecker", {
                          ...statusChecker,
                          timeToNextAttemptArray: newValue,
                        });
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  style={{
                    padding: "8px",
                    width: "max-content",
                    marginTop: "8px",
                  }}
                  onClick={() => {
                    changeBotPropery(key, "statusChecker", {
                      ...statusChecker,
                      timeToNextAttemptArray: [...timeToNextAttemptArray, 1],
                    });
                  }}
                >
                  Add
                </Button>
              </div>
            </Item.Content>
          </React.Fragment>
        )
      );
    },
    [botConfigs, changeBotPropery],
  );

  const renderPortalAccountCreation = useCallback(
    (key) => {
      if (!PORTAL_ACCOUNT_SUPPORTED_APIS.includes(key)) return null;
      const portalAccountCreation =
        botConfigs[key]?.portalAccountCreation || {};
      const enabled = !!portalAccountCreation.enabled;
      return (
        <React.Fragment>
          <Item.Content>
            <Item.Meta>Portal Account Creation</Item.Meta>
            <Item.Description className="bot-portal-account-content-wrapper">
              <div className="bot-portal-account-toggle">
                <Checkbox
                  toggle
                  onChange={(_, { checked }) =>
                    changeBotPropery(key, "portalAccountCreation", {
                      ...portalAccountCreation,
                      enabled: checked,
                    })
                  }
                  checked={enabled}
                />
                {enabled ? "enabled" : "disabled"}
                <span className="bot-portal-account-label">
                  Create portal account for paid rushed/urgent applications
                </span>
              </div>
            </Item.Description>
          </Item.Content>
        </React.Fragment>
      );
    },
    [botConfigs, changeBotPropery],
  );

  const renderRetryCap = useCallback(
    (key) => {
      const retryCap = botConfigs[key]?.retryCap || DEFAULT_RETRY_CAP;
      const enabled = !!retryCap.manualPause;
      const lastPauseChangeByUser =
        retryCap.lastPauseChangeBy &&
        allUsers.find((u) => u._id === retryCap.lastPauseChangeBy);
      const lastPauseChangeByLabel =
        lastPauseChangeByUser?.fullName ||
        lastPauseChangeByUser?.username ||
        lastPauseChangeByUser?.email ||
        retryCap.lastPauseChangeBy;

      const handleManualPauseChange = async (_, { checked }) => {
        setIsPausing(true);
        try {
          const { data } = await apis.botMonitoring.patchDs82BotStatusPause({
            manualPause: checked,
          });
          // Server-owned audit fields come from the response; fall back to
          // sensible client-side defaults in the unlikely event the response
          // shape shifts.
          const nextRetryCap = {
            ...retryCap,
            manualPause: data?.manualPaused ?? checked,
            lastPauseChangeAt: data?.lastPauseChangeAt ?? new Date().toISOString(),
            lastPauseChangeBy: data?.lastPauseChangeBy ?? currentUser?._id ?? null,
          };
          changeBotPropery(key, "retryCap", nextRetryCap);
          // Sync the discard baseline so Discard doesn't silently revert the
          // server-confirmed pause state back to the pre-toggle value.
          setDefaultBotConfigs({
            ...defaultBotConfigs,
            [key]: {
              ...defaultBotConfigs[key],
              retryCap: nextRetryCap,
            },
          });
          toastr.success(
            checked ? "DS82 bot cap paused." : "DS82 bot cap resumed.",
          );
        } catch (err) {
          logServerError(err);
          toastr.error("Failed to update DS82 cap.");
        } finally {
          setIsPausing(false);
        }
      };

      return (
        <React.Fragment>
          <Item.Content>
            <Item.Meta>Retry cap</Item.Meta>
            <Item.Description className="bot-portal-account-content-wrapper">
              <div className="bot-portal-account-toggle">
                <Checkbox
                  toggle
                  data-testid="ds82-retry-cap-toggle"
                  onChange={handleManualPauseChange}
                  checked={enabled}
                  disabled={isPausing}
                />
                {enabled ? "paused" : "active"}
                <span className="bot-portal-account-label">
                  Manually pause cap enforcement
                </span>
              </div>
              {enabled && retryCap.lastPauseChangeAt && (
                <div
                  className="bot-portal-account-label"
                  style={{ marginTop: "4px" }}
                >
                  Manually paused at {getDateFormat(retryCap.lastPauseChangeAt)}
                  {lastPauseChangeByLabel ? ` by ${lastPauseChangeByLabel}` : ""}
                </div>
              )}
            </Item.Description>
          </Item.Content>
        </React.Fragment>
      );
    },
    [
      botConfigs,
      changeBotPropery,
      allUsers,
      currentUser,
      isPausing,
      defaultBotConfigs,
      setDefaultBotConfigs,
    ],
  );

  return (
    <React.Fragment>
      <Item.Group className="bot-item-container">
        {Object.entries(filterInvalidProductProps(botConfigs)).map(
          ([key, botConfig]) => {
            return (
              <React.Fragment key={key}>
                <Item>
                  <Item.Content className="primary-card-container">
                    <Item.Header
                      style={{
                        color: "#00b5ad",
                        // backgroundColor: '#00b5ad',
                        display: "block",
                      }}
                    >
                      {key.toUpperCase()}
                    </Item.Header>
                    <Item.Meta>Primary credit card</Item.Meta>
                    <Item.Description>
                      <Select
                        button
                        className="icon"
                        floating
                        labeled
                        onChange={(event, field) =>
                          changeBotPropery(key, "creditCard", field.value)
                        }
                        value={botConfig.creditCard}
                        icon="credit card outline"
                        options={creditCardOptions}
                      />
                    </Item.Description>
                  </Item.Content>
                  <Item.Content className="secondary-card-container">
                    <Item.Meta>Secondary credit card</Item.Meta>
                    <Item.Description>
                      <Select
                        name={`secondary.${key}`}
                        button
                        className="icon"
                        floating
                        labeled
                        onChange={(event, field) =>
                          changeBotPropery(key, "alternativeCard", field.value)
                        }
                        value={botConfig.alternativeCard}
                        icon="credit card outline"
                        options={creditCardOptions}
                      />
                    </Item.Description>
                  </Item.Content>
                  {renderAutosubmitOptions(key)}
                </Item>
                <Item>{renderChecker(key)}</Item>
                {PORTAL_ACCOUNT_SUPPORTED_APIS.includes(key) && (
                  <Item>{renderPortalAccountCreation(key)}</Item>
                )}
                {key === "ds82" && <Item>{renderRetryCap(key)}</Item>}
              </React.Fragment>
            );
          },
        )}
      </Item.Group>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          margin: "12px",
        }}
      >
        <ConfirmPopup
          content="Are you sure you want discard changes?"
          callback={discardHandler}
        >
          <Button className="discard-button">Discard</Button>
        </ConfirmPopup>
        <ConfirmPopup
          content="Are you sure you want save changes?"
          callback={editBotConigurationHandler}
        >
          <Button className="save-button">Save</Button>
        </ConfirmPopup>
      </div>
    </React.Fragment>
  );
};

export default BotsConfiguration;
